import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AlignLeft,
  MessageSquare,
  Paperclip,
  CheckSquare,
  Clock,
  Eye,
  Bell,
} from "lucide-react";
import { ProjectCardData } from "containers/project-board/types";

// Match the reference: a single icon size and weight across every badge.
const ICON_SIZE = 15;
const ICON_STROKE = 1.75;

interface IconTextProps {
  title?: string;
  children: ReactNode;
  label?: number | string;
}

function IconText({ title, children, label }: IconTextProps) {
  return (
    <span title={title} className="inline-flex items-center gap-1 text-[var(--p-color-text-secondary)]">
      {children}
      {label !== undefined && label !== null && (
        <span className="text-[12px] leading-none">{label}</span>
      )}
    </span>
  );
}

function formatDue(dateString: string | null | undefined): string {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  // ISO calendar date (YYYY-MM-DD) from local parts — avoids the UTC shift that
  // toISOString() would introduce near midnight.
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface CardBadgesProps {
  card: ProjectCardData;
  /** Current user id — to detect "assigned to me but not yet seen". */
  currentUserId?: string;
}

// The due date sits in the card's top-right corner (see ProjectCard), separate
// from the metadata row below, so it reads at a glance and doesn't crowd the
// icon counts.
export function DueDateBadge({ card }: CardBadgesProps) {
  const { t } = useTranslation();
  const due = card.dueDate ? new Date(card.dueDate) : null;
  if (!due || Number.isNaN(due.getTime())) return null;
  const isClosed = !!card.outcome || card.isResolved;
  const isOverdue = !isClosed && due.getTime() < Date.now();

  return (
    <span
      title={t("due_date", { defaultValue: "Due date" })}
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-medium ${isOverdue
          ? "bg-[var(--p-color-bg-surface-critical)] text-[var(--p-color-text-critical)]"
          : "bg-[var(--p-color-bg-surface-secondary)] text-[var(--p-color-text-secondary)]"
        }`}
    >
      <Clock size={14} strokeWidth={ICON_STROKE} />
      {formatDue(card.dueDate)}
    </span>
  );
}

export default function CardBadges({ card, currentUserId }: CardBadgesProps) {
  const { t } = useTranslation();
  const hasDescription = !!card.description;

  // Eye = "you have seen this card" (sticky, per-user). Bell = the card is
  // assigned to you but you haven't opened it yet — a red "new assignment"
  // notification that turns into the eye once you open it. Mutually exclusive.
  const showEye = !!card.seen;
  const showBell =
    !showEye && !!currentUserId && !!card.user && card.user.id === currentUserId;

  const hasAny =
    showEye ||
    showBell ||
    hasDescription ||
    card.commentsCount > 0 ||
    card.filesCount > 0 ||
    card.productsCount > 0;

  if (!hasAny) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {showEye && (
        <IconText title={t("seen", { defaultValue: "Seen" })}>
          <Eye size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </IconText>
      )}
      {showBell && (
        <span
          title={t("assigned_to_you", { defaultValue: "Assigned to you" })}
          className="inline-flex items-center text-red-500"
        >
          <Bell size={ICON_SIZE} strokeWidth={ICON_STROKE} fill="currentColor" />
        </span>
      )}
      {hasDescription && (
        <IconText title={t("description", { defaultValue: "Description" })}>
          <AlignLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </IconText>
      )}
      {card.commentsCount > 0 && (
        <IconText title={t("comments", { defaultValue: "Comments" })} label={card.commentsCount}>
          <MessageSquare size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </IconText>
      )}
      {card.filesCount > 0 && (
        <IconText title={t("files", { defaultValue: "Attachments" })} label={card.filesCount}>
          <Paperclip size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </IconText>
      )}
      {card.productsCount > 0 && (
        <IconText title={t("products", { defaultValue: "Items" })} label={card.productsCount}>
          <CheckSquare size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </IconText>
      )}
    </div>
  );
}
